/* eslint-disable react/no-unused-prop-types */
import { groupBy } from 'lodash';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Menu, Typeahead, MenuItem, Token } from 'react-bootstrap-typeahead';
import withId from '../../../decorators/withId';
import { withTranslation } from '../../../translations/i18n';

export class GroupedMultiSelectInput extends Component {
  static propTypes = {
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.object,
      PropTypes.arrayOf(PropTypes.string),
      PropTypes.arrayOf(PropTypes.object),
    ]),
    options: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.arrayOf(PropTypes.object)]),
    disabled: PropTypes.bool,
    onChange: PropTypes.func,
    onInputChange: PropTypes.func,
    id: PropTypes.string.isRequired,
    t: PropTypes.func.isRequired,
    ariaLabel: PropTypes.string,
    required: PropTypes.bool,
    emptyLabel: PropTypes.string,
    placeholder: PropTypes.string,
    allowNew: PropTypes.bool,
    useCache: PropTypes.bool,
    newSelectionPrefix: PropTypes.string,
    filterOptions: PropTypes.func,
    isLoading: PropTypes.bool,
  };

  static defaultProps = {
    value: null,
    options: [],
    disabled: false,
    onChange: () => {},
    onInputChange: () => {},
    ariaLabel: null,
    required: false,
    emptyLabel: null,
    placeholder: null,
    filterOptions: null,
    allowNew: false,
    useCache: true,
    isLoading: false,
    newSelectionPrefix: null,
  };

  constructor(props) {
    super(props);

    this.state = {
      ...GroupedMultiSelectInput.getValueFromProps(props),
      inputText: '',
    };
    this.selectListInputRef = React.createRef();
    this.propertiesRef = React.createRef();
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    const { value } = nextProps;
    if (value !== prevState.previousPropValue) {
      return {
        ...GroupedMultiSelectInput.getValueFromProps(nextProps),
      };
    }
    return null;
  }

  componentDidMount() {
    const selectListInput = this.selectListInputRef.current;
    const propertiesRef = this.propertiesRef.current;
    if (selectListInput && propertiesRef) {
      const ariaOwns = propertiesRef.props['aria-owns'];
      selectListInput.setAttribute('id', ariaOwns);
    }
  }

  onInputChange = (inputText) => {
    this.setState({
      inputText,
    });

    this.props.onInputChange(inputText);
  };

  onChange = (value) => {
    const { onChange } = this.props;
    const { inputText } = this.state;

    const values = value.map(item => (item.value ? item.value : item));
    this.setState({
      value: values,
      inputText: value.length > 0 ? inputText : '',
    });
    onChange(values);
  };

  static getValueFromProps({ value }) {
    return {
      value: (value && ([...value])) || [],
      previousPropValue: value,
    };
  }

  static buildMenuItem(row, index, heading) {  //eslint-disable-line
    let className = '';
    if (heading === 'undefined') {
      className = 'GroupedSelectListInput--heading';
    }
    return (
      <MenuItem option={row} position={index} key={`${heading}-item-${index}`} className={className}>
        {row.text}
      </MenuItem>
    );
  }

  getTokenText(option, options) {
    if (typeof option === 'object' && 'text' in option) {
      return option.text;
    }
    if (options) {
      option = options.find(item => item.value === option);
      return option ? option.text : '';
    }
    return '';
  }

  filterOptions = (option) => {
    const { value, inputText } = this.state;
    if (value && value.includes(option.value)) {
      return false;
    }
    if (option.text === undefined) {
      return false;
    }
    return option.text.toLowerCase().indexOf(inputText.toLowerCase()) >= 0;
  };

  renderMenuWithGrouping(results, menuProps) {
    const grouped = groupBy(results, r => r.heading);
    const items = GroupedMultiSelectInput.renderGrouping(grouped);
    return (
      <Menu {...menuProps}>
        {items}
      </Menu>
    );
  }

  static renderGrouping(groupedResults) {
    let index = 0;
    const headings = Object.keys(groupedResults).sort();

    const items = [];
    headings.map((heading) => {  //eslint-disable-line
      let headingString = heading;
      if (heading === 'undefined') {
        headingString = '';
      }
      items.push((
        <Menu.Header key={`${heading}-header`} className="GroupedSelectListInput--heading">
          {headingString}
        </Menu.Header>
      ));
      const menuItems = [];
      groupedResults[heading].map((result) => {  //eslint-disable-line
        menuItems.push(GroupedMultiSelectInput.buildMenuItem(result, index, heading));
        index++;
      });
      items.push(menuItems);
    });
    return items;
  }

  renderToken = (option, props, index) => {
    const {
      options,
    } = this.props;
    const text = this.getTokenText(option, options);
    return (
      <Token key={index} onRemove={props.onRemove}>
        {`${text}`}
      </Token>
    );
  }

  render() {
    const {
      id,
      ariaLabel,
      required,
      disabled,
      options,
      emptyLabel,
      t,
      placeholder,
      allowNew,
      useCache,
      newSelectionPrefix,
      isLoading,
    } = this.props;
    const { value } = this.state;
    return (
      <div className="GroupedSelectListInput SelectListInput" ref={this.selectListInputRef}>
        <Typeahead
          allowNew={allowNew}
          id={id}
          disbaled={disabled}
          placeholder={placeholder}
          newSelectionPrefix={newSelectionPrefix || t('Search for ')}
          options={options}
          selected={value}
          multiple
          onChange={this.onChange}
          clearButton
          isLoading={isLoading}
          highlightOnlyResult={!allowNew}
          disabled={disabled}
          onInputChange={this.onInputChange}
          filterBy={this.filterOptions}
          useCache={useCache}
          labelKey="text"
          emptyLabel={emptyLabel || t('No matches found')}
          inputProps={{
            ref: this.propertiesRef,
            'data-id': id,
            'aria-label': ariaLabel,
            required,
            autoComplete: 'nofill',
            id,
          }}
          renderMenu={this.renderMenuWithGrouping}
          renderToken={this.renderToken}
        />
      </div>
    );
  }
}

const GroupedMultiSelectInputTranslated = withTranslation()(GroupedMultiSelectInput);

export default withId('GroupedMultiSelectInput')(GroupedMultiSelectInputTranslated);
